import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  keywords?: string[];
  noIndex?: boolean;
  brand?: "bbq";
  type?: "website" | "article" | "product";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
}

const brandNames = {
  bbq: "BBQ Island Liquidators",
};

export const SEOHead = ({
  title,
  description,
  canonical,
  ogImage,
  keywords,
  noIndex = false,
  brand,
  type = "website",
  publishedTime,
  modifiedTime,
  author,
}: SEOHeadProps) => {
  // Determine brand suffix
  const brandSuffix = brand ? brandNames[brand] : "The Liquidators";
  const fullTitle = title.includes("Liquidators") ? title : `${title} | ${brandSuffix}`;
  
  // Truncate description for SEO (max 160 chars)
  const truncatedDescription = description.length > 160 
    ? description.substring(0, 157) + "..." 
    : description;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={truncatedDescription} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Hreflang tags for international SEO */}
      {canonical && (
        <>
          <link rel="alternate" hrefLang="en-US" href={canonical} />
          <link rel="alternate" hrefLang="x-default" href={canonical} />
        </>
      )}
      
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDescription} />
      <meta property="og:site_name" content={brandSuffix} />
      <meta property="og:locale" content="en_US" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:alt" content={title} />}
      {canonical && <meta property="og:url" content={canonical} />}
      
      {/* Article-specific OG tags */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="US-TX" />
      <meta name="geo.placename" content="Dallas-Fort Worth" />
      <meta name="geo.position" content="32.9756;-96.8897" />
      <meta name="ICBM" content="32.9756, -96.8897" />
    </Helmet>
  );
};